import React from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import get from 'lodash/get';

import { Button, Link } from '@nerdwallet/currency';
import { withRouter } from 'react-router-dom';
import analytics from '@nerdwallet/analytics';
import { useAnalyticsPageView } from '@nerdwallet/seo-analytics';
import appConfig from '@nerdwallet/app-config';
import { FinishType } from '@nerdwallet/react-apply-now';
import SegmentImpression from '../segment-impression';
import AppButtons from '../app-buttons/app-buttons';

import {
  repeatable,
  stringInputSpec,
  stringTextAreaSpec,
  stringSelectSpec,
} from '../../lib/editing-configs';
import { getButtonStyle } from '../../lib/button-style-helper';
import {
  segmentImpressionId,
  trackSegmentTrackEvent,
} from '../../lib/segment-analytics';
import { baseSegmentAnalytics } from '../../lib/utils';
import { Auth0Buttons } from '../auth0-buttons/auth0-buttons';

export const NULL_BUTTON_TYPE = 'none';
export const CUSTOM_BUTTON_TYPE = 'custom';
const APP_STORE_BUTTON_TYPE = 'textMeApp';

const combinedSegmentAnalytics = {
  ...baseSegmentAnalytics,
  // eslint-disable-next-line no-underscore-dangle
  calling_deployable_name: appConfig?._meta?.deployable,
  // eslint-disable-next-line no-underscore-dangle
  calling_deployable_version: appConfig?._meta?.version,
};

const SmartButtonContent = ({
  buttonClass,
  buttonType,
  buttonText,
  buttonURL,
  successURL,
  authDialogCopyOverrides,
  authDialogImageOverrides,
  appStoreURL,
  googlePlayURL,
  buttonParams,
  displayBlock,
  link,
  primary,
  location,
}) => {
  if (buttonType === NULL_BUTTON_TYPE) {
    return null;
  }

  const buttonStyle = getButtonStyle(primary);

  const params = buttonParams.reduce(
    (paramDict, { queryKey, queryValue }) => ({
      ...paramDict,
      [queryKey]: queryValue,
    }),
    {}
  );

  if (buttonURL) {
    // Has user already included a query in their buttonURL?
    const [url, existingQuery = ''] = buttonURL.split('?');
    const newQuery =
      buttonParams.length > 0
        ? `?${existingQuery}&${queryString.stringify(params)}`
        : existingQuery && `?${existingQuery}`;
    const href = `${url}${newQuery}`;
    const handleSmartButtonClick = () => {
      trackSegmentTrackEvent('Element Interaction', {
        entity_name: 'smart_button',
        location: location?.pathname,
        sub_location: '',
        cta_type: 'apply_now_button',
        ...combinedSegmentAnalytics,
      });

      analytics.track('element_interacted', {
        action: 'select',
        entity_name: 'smart_button',
        impression_id: segmentImpressionId,
        element_name: 'apply_now_button',
        interaction_type: 'press',
        cta_type: 'offer_link',
        finish_type: FinishType.EXTERNAL_APPLICATION,
      });
    };

    return link ? (
      <Link
        href={href}
        className={buttonClass}
        onClick={handleSmartButtonClick}
      >
        {buttonText}
      </Link>
    ) : (
      <Button
        className={buttonClass}
        href={href}
        block={displayBlock}
        onClick={handleSmartButtonClick}
        {...buttonStyle}
      >
        {buttonText}
      </Button>
    );
  }

  if (buttonType === APP_STORE_BUTTON_TYPE) {
    return (
      <AppButtons appStoreURL={appStoreURL} googlePlayURL={googlePlayURL} />
    );
  }

  const flowProps = {
    trk: params,
    flowConfig: {},
  };

  if (successURL) {
    flowProps.onSuccess = successURL;
  }

  if (authDialogCopyOverrides) {
    flowProps.flowConfig.copyOverrides = {};
    authDialogCopyOverrides.forEach((copyOverride) => {
      flowProps.flowConfig.copyOverrides[copyOverride.copyKey] =
        copyOverride.copyValue;
    });
  }

  if (authDialogImageOverrides) {
    flowProps.flowConfig.imageOverrides = {};
    authDialogImageOverrides.forEach((imageOverride) => {
      flowProps.flowConfig.imageOverrides[imageOverride.imageKey] =
        imageOverride.imageValue;
    });
  }

  return (
    <Auth0Buttons.Container displayBlock={displayBlock}>
      <Auth0Buttons.GenericButton
        redirectTo={successURL}
        customCtaText={buttonText}
        buttonStyle={{ ...buttonStyle }}
        displayBlock={displayBlock}
        className={buttonClass}
      />
    </Auth0Buttons.Container>
  );
};

const SmartButton = (props) => {
  const { location } = props;
  const { pageViewId } = useAnalyticsPageView();

  const segmentImpressionProps = {
    ...baseSegmentAnalytics,
    impression_id: segmentImpressionId,
    impression_page_url: location?.pathname,
    impression_pageview_id: pageViewId,
    sku_impression_id: segmentImpressionId,
    // eslint-disable-next-line no-underscore-dangle
    calling_deployable_name: appConfig?._meta?.deployable,
    // eslint-disable-next-line no-underscore-dangle
    calling_deployable_version: appConfig?._meta?.version,
  };
  return (
    <SegmentImpression
      eventName="MarketplaceCta Viewed"
      eventProps={segmentImpressionProps}
    >
      <SmartButtonContent {...props} />
    </SegmentImpression>
  );
};

SmartButton.displayName = 'SmartButton';

SmartButton.propTypes = {
  buttonClass: PropTypes.string,
  buttonType: PropTypes.string,
  buttonText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  buttonURL: PropTypes.string,
  successURL: PropTypes.string,
  authDialogCopyOverrides: PropTypes.arrayOf(
    PropTypes.shape({
      copyKey: PropTypes.string,
      copyValue: PropTypes.string,
    })
  ),
  authDialogImageOverrides: PropTypes.arrayOf(
    PropTypes.shape({
      imageKey: PropTypes.string,
      imageValue: PropTypes.string,
    })
  ),
  buttonParams: PropTypes.array,
  displayBlock: PropTypes.bool,
  link: PropTypes.bool,
  primary: PropTypes.string,
  appStoreURL: PropTypes.string,
  googlePlayURL: PropTypes.string,
};

SmartButton.defaultProps = {
  buttonType: NULL_BUTTON_TYPE,
  buttonParams: [],
  displayBlock: false,
  link: false,
  primary: 'primary',
};

SmartButtonContent.propTypes = {
  ...SmartButton.propTypes,
};

SmartButtonContent.defaultProps = {
  ...SmartButton.defaultProps,
};

SmartButton.getEditingConfig = (componentConfig) => {
  const buttonType = get(componentConfig, 'props.buttonType');
  const isAuthDialog =
    buttonType &&
    buttonType !== NULL_BUTTON_TYPE &&
    buttonType !== CUSTOM_BUTTON_TYPE &&
    buttonType !== APP_STORE_BUTTON_TYPE;
  const successURLSpec = isAuthDialog
    ? [
        stringInputSpec('successURL', 'CTA on success url', {
          help: 'For Dialog CTAs, the URL to take the user after they have successfully registered',
        }),
      ]
    : [];

  const authDialogCopyOverridesSpec = isAuthDialog
    ? [
        repeatable(
          'authDialogCopyOverrides',
          'Dialog copy overrides',
          'Param',
          () => [
            stringInputSpec('copyKey', 'Key'),
            stringTextAreaSpec('copyValue', 'Value'),
          ]
        ),
      ]
    : [];

  const authDialogImageOverridesSpec = isAuthDialog
    ? [
        repeatable(
          'authDialogImageOverrides',
          'Dialog image overrides',
          'Param',
          () => [
            stringInputSpec('imageKey', 'Key'),
            stringTextAreaSpec('imageValue', 'Value'),
          ]
        ),
      ]
    : [];

  const appStoreUrlOverridesSpec =
    buttonType === APP_STORE_BUTTON_TYPE ? AppButtons.getEditingConfig() : [];

  return [
    stringSelectSpec('buttonType', 'CTA', [
      {
        label: 'None',
        value: NULL_BUTTON_TYPE,
      },
      {
        label: 'Custom URL',
        value: CUSTOM_BUTTON_TYPE,
      },
      {
        label: 'Dialog: Generic Onboarding',
        value: 'onboardingGeneric',
      },
      {
        label: 'Dialog: Credit Score',
        value: 'creditScore',
      },
      {
        label: 'Dialog: BillShark',
        value: 'bills',
      },
      {
        label: 'Dialog: Emergency Fund Goal',
        value: 'emergencyFundGoal',
      },
      {
        label: 'Dialog: Savings Goal',
        value: 'savingsGoal',
      },
      {
        label: 'Dialog: Retirement Goal',
        value: 'retirementGoal',
      },
      {
        label: 'Dialog: Debt Goal',
        value: 'debtGoal',
      },
      {
        label: 'Dialog: Credit Score Goal',
        value: 'creditGoal',
      },
      {
        label: 'Dialog: Cash Back',
        value: 'cashBack',
      },
      {
        label: 'App Download Buttons',
        value: APP_STORE_BUTTON_TYPE,
      },
    ]),
    stringTextAreaSpec('buttonText', 'CTA Text'),
    ...successURLSpec,
    ...authDialogCopyOverridesSpec,
    ...authDialogImageOverridesSpec,
    ...appStoreUrlOverridesSpec,
    stringTextAreaSpec('buttonURL', 'Custom button target url', {
      help: 'URL for the user to be redirected to on click. Be sure to add tracking params (e.g. www.a.com?trk_app=landing-page-client)',
    }),
    stringSelectSpec(
      'primary',
      'Smart Button Style',
      [
        { label: 'Select an option', value: '' },
        { label: 'Primary', value: 'primary' },
        { label: 'Secondary', value: 'secondary' },
        { label: 'Tertiary', value: 'tertiary' },
      ],
      {
        help: 'Primary output a GREEN button with WHITE text; Secondary output a WHITE button with GREEN text, Tertiary output a WHITE button without border',
      }
    ),
    repeatable('buttonParams', 'Tracking Params', 'Param', () => [
      stringInputSpec('queryKey', 'Key'),
      stringTextAreaSpec('queryValue', 'Value'),
    ]),
  ];
};

export default withRouter(SmartButton);
